// The plan panel, beside the conversation rather than behind a tab. Desktop only.
//
// `docs/archived/13-visual-refresh.md` leaves the old `Decisions` and `People` routes and tab row
// entirely: on desktop this is a persistent, collapsible panel — a flex sibling of the
// conversation column, not an overlay — holding the plan's objective, its roster, and its
// decisions (`Decisions.tsx`) as three sections rather than three separate destinations. It
// is hidden by CSS below the desktop breakpoint (`.decisionsPanel`, `styles.css`), where
// `DecisionsMobileStrip` stands in for it instead.
//
// The two are separate components, not one component that repositions itself with CSS,
// because they render in different places in the tree: this one is a `.planBody` sibling
// of the conversation column; the mobile strip has to sit INSIDE `Conversation`'s and
// `PrivateChat`'s own footer, directly above the composer, which is a place only those two
// components can put something (see `DecisionsMobileStrip.tsx`'s header comment).
//
// `09-web-client.md`'s "visible to everyone in the plan at any time" is satisfied by this
// being one interaction from wherever you're reading — a click to expand the panel, never a
// second navigation away from the conversation.
//
// The open head carries no title: a single word could not name three sections honestly,
// and the sections already name themselves with their own eyebrows. The collapsed reopen
// tab keeps saying "Decisions" with the open-decision count, because that count is still
// the single most useful thing to glance at from outside the panel.
//
// ---------------------------------------------------------------------------------
// A SECOND DRAWER, ON THE SAME SHELL. In a demo plan, the seat's brief (`CharacterBrief.tsx`,
// shared with `CharacterSheet.tsx`) used to be an "About Daniel" link in the plan header
// (`PlanScreen.tsx`) — jammed against this panel's own reopen tab, two different shapes
// competing for the same corner. It is a second drawer now, with its own tab beneath the
// Decisions tab, rather than a second shape in the header.
//
// It is a second drawer, not a second COMPONENT, alongside this one: this one component
// renders both, because only one of the two can ever be open at a time, and because
// `.planLayout` lays this shell out as a single flex item in a row against the
// conversation column. Two independent components, each pinned flush to the right edge
// with `margin-left: auto` the way the reopen tab always has been, would sit side by
// side in that row when both are collapsed — not stacked, one beneath the other, the way
// this drawer's tab is supposed to sit beneath the Decisions tab. Nesting both tabs
// inside the one collapsed shell (`.decisionsPanel--collapsed`, now `flex-direction:
// column` — see `styles.css`) is what actually stacks them; that is the one change made
// to the existing rules rather than a parallel set, because this shell IS the existing
// drawer, wearing a second tab.
//
// ONE DRAWER AT A TIME, AND WHY. Opening either drawer closes the other, and while either
// is open the closed one is not offered at all — not merely disabled — its tab simply
// does not render. Two panels each covering part of the conversation on a narrow desktop
// window would leave nothing of the conversation to read; the only way to see the other
// is to close the one that's open first, the same one motion a person already has to
// make to look at the conversation behind either.
//
// The about tab only ever appears in a demo plan (`isDemo`), the same gate the header
// link it replaced used, and it is labelled the same way that link was: the seat's first
// name when `useDemoSeatIntro` has resolved one, or the generic "Character sheet"
// otherwise — see `PlanScreen.tsx` for where that hook is called.

import { useState } from 'react';

import type { ApiClient } from '../api/client.js';
import type { DemoSeatIntro } from '../demo/useDemoSeatIntro.js';
import type { Internals } from '../internals.js';
import type { PlanState } from '../plan/usePlan.js';
import { Decisions } from '../surfaces/Decisions.js';
import { welcomeParagraphs } from '../demo/welcome-text.js';
import { CharacterBrief } from './CharacterBrief.js';

export type DecisionsPanelProps = {
  state: PlanState;
  internals: Internals;
  client: ApiClient;
  planId: string;
  /** Starts open on a direct link to the old `/decisions` or `/people` route — see
   * `PlanScreen.tsx`'s header comment. Defaults to collapsed otherwise. Nothing links
   * directly to the about drawer, so it never starts open this way. */
  initialOpen?: boolean;
  /** Whether this plan is a demo — the about drawer's tab exists only on one, the same
   *  gate the header link it replaced used. */
  isDemo: boolean;
  /** The seat this demo was started from, resolved by `useDemoSeatIntro` — `null` for
   *  every shape of "nothing to name it with" (not yet resolved, or nothing recorded),
   *  in which case the tab still appears, on the same generic fallback label the header
   *  link it replaced used. */
  demoSeat: DemoSeatIntro | null;
};

/* Points right, toward the edge the panel collapses into — not left, which reads as
 * opening or going back rather than dismissing. */
function ChevronIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="1rem" height="1rem" aria-hidden="true">
      <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

/** Which of the two drawers is open, if either. `null` is both collapsed — the shell's
 *  starting state everywhere except a direct link into the decisions drawer. */
type OpenDrawer = 'decisions' | 'about' | null;

export function DecisionsPanel({
  state,
  internals,
  client,
  planId,
  initialOpen = false,
  isDemo,
  demoSeat,
}: DecisionsPanelProps): JSX.Element | null {
  const [open, setOpen] = useState<OpenDrawer>(initialOpen ? 'decisions' : null);
  const snapshot = state.snapshot;
  if (snapshot === null) {
    return null;
  }

  const openCount = snapshot.decisions.filter(
    (decision) => decision.state !== 'done',
  ).length;
  // "Demo", not the character's name: the drawer is about the demonstration — what this
  // scenario is, that everyone else is played by a model — and who you are in it is one
  // part of that rather than the whole.
  const aboutLabel = 'Demo';

  if (open === null) {
    return (
      <aside className="decisionsPanel decisionsPanel--collapsed" aria-label="Plan">
        <button
          type="button"
          className="decisionsPanel__reopen"
          onClick={() => setOpen('decisions')}
        >
          Decisions
          <span className="decisionsPanel__badge">{openCount}</span>
        </button>
        {isDemo ? (
          <button
            type="button"
            className="decisionsPanel__reopen"
            onClick={() => setOpen('about')}
          >
            {aboutLabel}
          </button>
        ) : null}
      </aside>
    );
  }

  return (
    <aside
      className="decisionsPanel"
      aria-label={open === 'decisions' ? 'Plan' : aboutLabel}
    >
      <div className="decisionsPanel__head">
        <button
          type="button"
          className="iconButton decisionsPanel__collapse"
          aria-label={open === 'decisions' ? 'Hide decisions' : 'Hide character brief'}
          onClick={() => setOpen(null)}
        >
          <ChevronIcon />
        </button>
      </div>
      {open === 'decisions' ? (
        <Decisions state={state} internals={internals} client={client} planId={planId} />
      ) : (
        <div className="characterDrawer">
          {/* What this demonstration is, first — the scenario's own words, the same ones the
              welcome uses, so there is one place to write them. Its first entry is the
              "You're …" line, which is the seat's own heading below rather than standing
              text, so it is left out here. */}
          {demoSeat === null
            ? null
            : welcomeParagraphs(demoSeat).map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
          {/* The second half names itself. "About Daniel" rather than "Daniel, the father",
              which the welcome above has already said — this heading's job is to say that
              what follows is the longer account of who you are, not to introduce the person
              again. */}
          {demoSeat === null ? null : (
            <h2 className="characterDrawer__title">About {demoSeat.firstName}</h2>
          )}
          <CharacterBrief
            client={client}
            planId={planId}
            className="characterDrawer__body"
          />
        </div>
      )}
    </aside>
  );
}
